/**
 * Manages interactive elements in the header, such as dropdown menus and the search panel.
 * This script handles showing/hiding panels on click, manages ARIA states for accessibility,
 * controls keyboard navigation (Enter, Space, Escape), and handles focus management
 * for an accessible user experience.
 */
export function initializeInteractiveHeader() {
    const header = document.getElementById('header');
    if (!header) return;

    const nav = header.querySelector('.header__navigation'); // The mobile nav container
    const searchToggle = header.querySelector('.header__search-toggle');
    const searchContainer = header.querySelector('.header__search-container');
    const searchForm = header.querySelector('#header-search-form');
    const searchInput = header.querySelector('#header-search-input');
    const menuPanel = header.querySelector('#header-menu-panel');
    const menuToggle = header.querySelector('.header__menu-toggle');

    if (!menuPanel) {
        console.warn('Header menu panel not found. Sub-menu backgrounds will not work.');
    }

    const allPanels = Array.from(header.querySelectorAll('.sub-menu, .header__search-form'));
    let activeTrigger = null; // To store the element that opened a panel for focus return

    // Set initial ARIA states for accessibility
    allPanels.forEach(panel => panel.setAttribute('aria-hidden', 'true'));
    if (searchToggle) searchToggle.setAttribute('aria-expanded', 'false');

    /**
     * Opens the mobile navigation menu and sets its height based on content.
     */
    const openMobileMenu = () => {
        if (!nav || !menuToggle) return;
        const menu = nav.querySelector('ul');
        if (!menu) {
            console.warn('Could not find menu list (ul) inside .header__navigation to calculate height.');
            return;
        }

        document.body.classList.add('menu-open');
        menuToggle.setAttribute('aria-expanded', 'true');

        // Use scrollHeight to get the full height of the content
        const menuHeight = menu.scrollHeight;
        nav.style.height = `${menuHeight}px`;
    };

    /**
     * Closes the mobile navigation menu and resets its height.
     */
    const closeMobileMenu = () => {
        if (!nav || !menuToggle) return;
        document.body.classList.remove('menu-open');
        menuToggle.setAttribute('aria-expanded', 'false');
        if (nav) {
            nav.style.height = '0px';
        }
    };

    /**
     * Hides all panels, resets ARIA states, and returns focus to the trigger.
     * @param {object} options - Optional parameters.
     * @param {boolean} options.keepMobileOpen - If true, the mobile menu will not be closed.
     */
    const hideAllPanels = (options = {}) => {
        const { keepMobileOpen = false } = options;

        allPanels.forEach(panel => {
            panel.classList.remove('is-visible');
            panel.setAttribute('aria-hidden', 'true');
        });

        if (activeTrigger) {
            activeTrigger.setAttribute('aria-expanded', 'false');
            // Return focus to the element that opened the panel
            activeTrigger.focus();
            activeTrigger = null;
        }

        if (menuPanel) {
            menuPanel.style.height = '0px';
        }
        header.classList.remove('is-panel-active');

        // Also ensure the mobile menu is closed if it exists and is open.
        if (!keepMobileOpen && menuToggle && document.body.classList.contains('menu-open')) {
            closeMobileMenu();
        }
    };

    /**
     * Shows a specific panel, sets ARIA states, and manages focus.
     * @param {HTMLElement} panel The panel element to show.
     * @param {HTMLElement} trigger The element that triggered the panel to open.
     */
    const showPanel = (panel, trigger) => {
        // Close mobile menu if it's open, to ensure menus are mutually exclusive.
        hideAllPanels();

        // Hide any other visible panel first
        const currentlyVisible = allPanels.find(p => p.classList.contains('is-visible') && p !== panel);
        if (currentlyVisible) {
            currentlyVisible.classList.remove('is-visible');
            currentlyVisible.setAttribute('aria-hidden', 'true');
            if (activeTrigger) {
                activeTrigger.setAttribute('aria-expanded', 'false');
            }
        }

        panel.classList.add('is-visible');
        panel.setAttribute('aria-hidden', 'false');

        // Update the trigger
        activeTrigger = trigger;
        if (activeTrigger) {
            activeTrigger.setAttribute('aria-expanded', 'true');
        }

        // Calculate and set the background panel height using scrollHeight
        if (menuPanel) {
            const panelHeight = panel.scrollHeight;
            menuPanel.style.height = `${panelHeight}px`;
            header.classList.add('is-panel-active');
        }

        // --- Focus Management ---
        // Use a timeout to ensure the panel is visible and focusable before moving focus.
        setTimeout(() => {
            if (panel === searchForm && searchInput) {
                searchInput.focus();
            } else {
                const firstFocusable = panel.querySelector('a, button');
                if (firstFocusable) {
                    firstFocusable.focus();
                }
            }
        }, 100); // A small delay can help, especially with transitions.
    };

    // --- Sub-menu click & keydown logic ---
    if (nav) {
        const menuItemsWithChildren = Array.from(nav.querySelectorAll('.menu-item-has-children'));

        menuItemsWithChildren.forEach(item => {
            const link = item.querySelector('a[aria-haspopup="true"]');
            if (!link) return;

            const subMenuId = link.getAttribute('aria-controls');
            const subMenu = document.getElementById(subMenuId);
            if (!subMenu) return;

            const toggleSubMenu = (event) => {
                // This is the crucial part to stop the browser's default navigation
                event.preventDefault();

                const isVisible = subMenu.classList.contains('is-visible');

                if (isVisible) {
                    hideAllPanels();
                } else {
                    showPanel(subMenu, link);
                }
            };

            link.addEventListener('click', toggleSubMenu);

            link.addEventListener('keydown', (event) => {
                if (event.key === 'Enter' || event.key === ' ') { // ' ' is for Space key
                    toggleSubMenu(event);
                }
            });
        });
    }

    // --- Search toggle click logic ---
    if (searchToggle && searchForm) {
        searchToggle.addEventListener('click', () => {
            const isVisible = searchForm.classList.contains('is-visible');
            if (isVisible) {
                hideAllPanels();
            } else {
                showPanel(searchForm, searchToggle);
            }
        });
    }

    // --- Mobile menu toggle click logic ---
    if (menuToggle) {
        menuToggle.addEventListener('click', (e) => {
            e.preventDefault();
            const isCurrentlyOpen = document.body.classList.contains('menu-open');

            // Always close any open desktop panels when interacting with the mobile toggle.
            // hideAllPanels() will also close the mobile menu if it was already open.
            hideAllPanels();

            // If the mobile menu was closed, we now open it.
            // If it was open, hideAllPanels() has already taken care of closing it.
            if (!isCurrentlyOpen) {
                openMobileMenu();
            }
        });
    }

    /**
     * Recalculates and sets the mobile menu height. Essential for orientation changes.
     */
    const updateMobileMenuHeight = () => {
        if (document.body.classList.contains('menu-open') && nav) {
            const menu = nav.querySelector('ul');
            if (menu) {
                // Disable transitions for instant recalculation to avoid visual glitches
                nav.style.transition = 'none';
                // Set to auto to measure the natural height after a potential resize
                nav.style.height = 'auto';
                const newHeight = menu.scrollHeight;
                nav.style.height = `${newHeight}px`;
                // Re-enable transitions after the new height is applied
                // Using a timeout to ensure the browser has rendered the change
                setTimeout(() => {
                    nav.style.transition = '';
                }, 50);
            }
        }
    };

    // --- Global event listeners to close panels ---
    document.addEventListener('click', (event) => {
        // If no panel is open, there's nothing to do.
        const isAnyPanelVisible = allPanels.some(p => p.classList.contains('is-visible'));
        if (!isAnyPanelVisible) {
            return;
        }

        // Hide panels if the click is outside the main interactive areas:
        // the navigation and the search container.
        if (
            (!nav || !nav.contains(event.target)) &&
            (!searchContainer || !searchContainer.contains(event.target))
        ) {
            hideAllPanels();
        }
    });

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            const isAnyPanelVisible = allPanels.some(p => p.classList.contains('is-visible'));
            if (isAnyPanelVisible) {
                event.preventDefault(); // Prevent other escape actions if a panel is open
                hideAllPanels();
            }
        }
    });

    // Listen for the custom event dispatched by Barba.js to close panels before a page transition.
    document.addEventListener('barba-leave-start', () => {
        hideAllPanels();
    });

    /**
     * Handles window resize and orientation changes.
     * Closes desktop dropdowns but recalculates mobile menu height to keep it open.
     */
    const handleResize = () => {
        // Close desktop panels, but keep mobile menu state intact.
        hideAllPanels({ keepMobileOpen: true });
        // Recalculate height for the open mobile menu.
        updateMobileMenuHeight();
    };

    // On resize or orientation change, close desktop panels and readjust mobile menu height.
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
}